import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Send, ChevronLeft, Share2, Mail, LogOut, Archive, MessageSquare, Check,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { UI, t } from "@/i18n/translations";
import { api } from "@/lib/api";

export function distanceLabel(km, lang) {
  const N = UI.reseauMod;
  if (km == null) return t(N.sameCity, lang);
  if (km < 0.6) return t(N.sameArea, lang);
  return lang === "fr" ? `à ~${km} km` : `~${km} km away`;
}

export function Messages({ onChanged }) {
  const { lang } = useLang();
  const N = UI.reseauMod;
  const [conversations, setConversations] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(() => {
    api.get("/reseau/messages")
      .then(({ data }) => setConversations(data.conversations))
      .catch(() => setConversations([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (activeId) return;
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [activeId, load]);

  if (activeId) {
    return (
      <Thread
        convId={activeId}
        onBack={() => { setActiveId(null); load(); onChanged?.(); }}
      />
    );
  }

  if (conversations === null) {
    return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber" /></div>;
  }

  if (conversations.length === 0) {
    return (
      <div className="rounded-[18px] border border-dashed border-line bg-white/50 p-8 text-center text-sm text-ink/50" data-testid="no-conversations">
        {t(N.noConversations, lang)}
      </div>
    );
  }

  return (
    <div className="space-y-2.5" data-testid="conversations-list">
      {conversations.map((c) => (
        <button
          key={c.conv_id}
          data-testid={`conversation-${c.conv_id}`}
          onClick={() => setActiveId(c.conv_id)}
          className="w-full text-left rounded-[18px] bg-white border border-line p-4 flex items-center gap-3 hover:border-amber transition-colors"
        >
          <div className="grid place-items-center w-11 h-11 rounded-full bg-amber/15 shrink-0 overflow-hidden">
            {c.other.picture ? <img src={c.other.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-display font-bold text-amber">{(c.other.prenom || "?")[0]}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-display font-bold">{c.other.prenom}</span>
              {c.archived && <Archive className="w-3.5 h-3.5 text-ink/35" />}
            </div>
            <div className="text-xs text-ink/50 truncate">{c.last_message || "…"}</div>
          </div>
          {c.unread > 0 && (
            <span className="shrink-0 grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-coral text-white text-[11px] font-bold" data-testid={`unread-${c.conv_id}`}>
              {c.unread}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function Thread({ convId, onBack }) {
  const { lang } = useLang();
  const { user } = useAuth();
  const N = UI.reseauMod;
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/reseau/messages/${convId}`).then(({ data }) => setData(data)).catch(() => {});
  }, [convId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 7000);
    return () => clearInterval(iv);
  }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    const body = text;
    setText("");
    try {
      const { data: msg } = await api.post(`/reseau/messages/${convId}`, { text: body });
      setData((d) => ({ ...d, messages: [...d.messages, msg] }));
    } catch (e) {
      setText(body);
      toast.error(e.response?.data?.detail || UI.common.error[lang]);
    } finally {
      setSending(false);
    }
  };

  const shareContact = async () => {
    try {
      await api.post(`/reseau/messages/${convId}/share-contact`);
      setData((d) => ({ ...d, my_contact_shared: true }));
      toast.success(t(N.contactShared, lang));
    } catch { toast.error(UI.common.error[lang]); }
  };

  const endRelation = async () => {
    try {
      await api.post(`/reseau/messages/${convId}/end`);
      toast.success(t(N.ended, lang));
      onBack();
    } catch { toast.error(UI.common.error[lang]); }
  };

  if (!data) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber" /></div>;

  return (
    <div className="flex flex-col h-[calc(100vh-13rem)]" data-testid="chat-thread">
      {/* header */}
      <div className="flex items-center gap-3 pb-3 border-b border-line">
        <button data-testid="chat-back-btn" onClick={onBack} className="grid place-items-center w-9 h-9 rounded-full border border-line bg-white"><ChevronLeft className="w-5 h-5" /></button>
        <div className="grid place-items-center w-9 h-9 rounded-full bg-amber/15 overflow-hidden">
          {data.other.picture ? <img src={data.other.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-display font-bold text-amber text-sm">{(data.other.prenom || "?")[0]}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold leading-tight">{data.other.prenom}</div>
          <div className="text-xs text-ink/45">{distanceLabel(data.other.distance_km, lang)}</div>
        </div>
        {!data.archived && (
          <button data-testid="end-relation-btn" onClick={endRelation} title={t(N.endRelationship, lang)} className="grid place-items-center w-9 h-9 rounded-full border border-line bg-white text-coral hover:bg-coral/8">
            <LogOut className="w-4.5 h-4.5" />
          </button>
        )}
      </div>

      {/* other contact if shared */}
      {data.other_contact && (
        <a href={`mailto:${data.other_contact.email}`} data-testid="their-contact" className="mt-3 flex items-center gap-2 rounded-2xl bg-sage/10 text-sage font-semibold text-sm px-3 py-2.5">
          <Mail className="w-4 h-4" /> {t(N.theirContact, lang)} {data.other_contact.email}
        </a>
      )}

      {/* messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-2.5 no-scrollbar">
        {data.messages.map((m) => {
          if (m.kind === "system") {
            return <div key={m.msg_id} className="text-center text-xs text-ink/40 py-2">{t(N.endedSystem, lang)}</div>;
          }
          const mine = m.from_user_id === user?.user_id;
          return (
            <div key={m.msg_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm ${mine ? "bg-ink text-paper rounded-br-md" : "bg-white border border-line rounded-bl-md"}`}>
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-paper/50" : "text-ink/35"}`}>
                  {new Date(m.created_at).toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-GB", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* footer */}
      {data.archived ? (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-ink/50 border-t border-line">
          <Archive className="w-4 h-4" /> {t(N.archivedLabel, lang)}
        </div>
      ) : (
        <div className="pt-2 border-t border-line space-y-2">
          {!data.my_contact_shared ? (
            <button data-testid="share-contact-btn" onClick={shareContact} className="w-full rounded-2xl border border-line bg-paper py-2.5 text-xs font-semibold flex items-center justify-center gap-2 text-ink/70 hover:border-amber transition-colors">
              <Share2 className="w-4 h-4 text-amber" /> {t(N.shareContact, lang)}
            </button>
          ) : (
            <div className="w-full rounded-2xl bg-sage/10 py-2.5 text-xs font-semibold flex items-center justify-center gap-2 text-sage">
              <Check className="w-4 h-4" /> {t(N.contactShared, lang)}
            </div>
          )}
          <div className="flex gap-2">
            <input
              data-testid="message-input"
              value={text}
              maxLength={2000}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
              placeholder={t(N.typeMessage, lang)}
              className="flex-1 rounded-2xl border border-line bg-white px-4 py-3 text-sm outline-none focus:border-amber"
            />
            <button data-testid="send-message-btn" onClick={send} disabled={sending || !text.trim()} className="shrink-0 grid place-items-center w-12 rounded-2xl bg-amber text-ink disabled:opacity-50">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
